/**
 * WorkflowEditor — Visual step-pipeline editor for sales workflows.
 *
 * @page /sales/workflows/:id
 * @roles admin, manager
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import useTabParam from '../../hooks/useTabParam';
import { useLanguage } from '../../context/LanguageContext';
import { ToggleSwitch } from '../../components/ui';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TiptapUnderline from '@tiptap/extension-underline';
import {
 ArrowLeft, Save, Play, Plus, X, GripVertical, Clock, Mail,
 CheckSquare, ArrowRight, Edit3, Zap, GitBranch, MousePointer,
 Trash2, ChevronDown, Bold, Italic, Underline as UnderlineIcon,
 List, Eye, Paperclip, Filter, Square, AlertTriangle, CheckCircle,
 XCircle, Activity, Search, RotateCcw, Sparkles, Send
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function api(path, opts = {}) {
 const hasBody = opts.body != null;
 const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
 return fetch(`${API}${path}`, {
 ...opts, credentials: 'include',
 headers: { ...headers, ...opts.headers },
 body: hasBody ? JSON.stringify(opts.body) : undefined,
 }).then(async (r) => {
 const data = await r.json();
 if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
 return data;
 });
}

// ═══════════════════════════════════════════════════════════════
// STEP TYPES
// ═══════════════════════════════════════════════════════════════

const STEP_TYPES = {
 delay: { label: 'delay', icon: Clock, color: '#F59E0B' },
 email: { label: 'email', icon: Mail, color: '#338edc' },
 task: { label: 'task', icon: CheckSquare, color: '#10B981' },
 stage: { label: 'stage', icon: ArrowRight, color: '#6366F1' },
 field: { label: 'field', icon: Edit3, color: '#22D3EE' },
 webhook: { label: 'webhook', icon: Zap, color: '#EF4444' },
 condition: { label: 'condition', icon: GitBranch, color: '#F97316' },
};

const TRIGGERS = [
 { id: 'stage_change', icon: ArrowRight },
 { id: 'lead_created', icon: Plus },
 { id: 'manual', icon: Play },
 { id: 'score_threshold', icon: Zap },
 { id: 'no_activity', icon: Clock },
 { id: 'email_reply', icon: Mail },
];

const STAGES = ['Neu', 'Kontaktiert', 'Termin', 'Angebot', 'Gewonnen', 'Verloren', 'Kalt'];

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

function generateStepId() {
 return 's' + Math.random().toString(36).substr(2, 6);
}

function getStepByPath(steps, path) {
 if (!path || !steps) return null;
 const parts = path.split('.');
 let current = steps;
 for (const part of parts) {
 if (part === 'yes' || part === 'no') {
 current = current?.[part];
 } else {
 current = current?.[parseInt(part)];
 }
 if (!current) return null;
 }
 return current;
}

function updateStepAtPath(steps, path, updates) {
 const newSteps = JSON.parse(JSON.stringify(steps));
 const parts = path.split('.');
 let current = newSteps;

 for (let i = 0; i < parts.length - 1; i++) {
 const part = parts[i];
 current = (part === 'yes' || part === 'no') ? current[part] : current[parseInt(part)];
 }

 const lastPart = parts[parts.length - 1];
 if (lastPart === 'yes' || lastPart === 'no') return newSteps;
 const idx = parseInt(lastPart);
 if (typeof updates === 'function') {
 current[idx] = updates(current[idx]);
 } else {
 current[idx] = { ...current[idx], ...updates };
 }
 return newSteps;
}

function insertStepAfterPath(steps, afterPath, type) {
 const newSteps = JSON.parse(JSON.stringify(steps));
 const newStep = { id: generateStepId(), type, config: getDefaultConfig(type) };
 if (type === 'condition') { newStep.yes = []; newStep.no = []; }

 if (afterPath === '-1') {
 newSteps.unshift(newStep);
 return newSteps;
 }

 const parts = afterPath.split('.');
 let target = newSteps;
 for (let i = 0; i < parts.length - 1; i++) {
 const part = parts[i];
 target = (part === 'yes' || part === 'no') ? target[part] : target[parseInt(part)];
 }

 const lastPart = parts[parts.length - 1];
 if (lastPart === 'yes' || lastPart === 'no') {
 if (!target[lastPart]) target[lastPart] = [];
 target[lastPart].push(newStep);
 } else if (Array.isArray(target)) {
 target.splice(parseInt(lastPart) + 1, 0, newStep);
 }
 return newSteps;
}

function removeStepAtPath(steps, path) {
 const newSteps = JSON.parse(JSON.stringify(steps));
 const parts = path.split('.');
 let target = newSteps;

 for (let i = 0; i < parts.length - 1; i++) {
 const part = parts[i];
 target = (part === 'yes' || part === 'no') ? target[part] : target[parseInt(part)];
 }

 const lastPart = parts[parts.length - 1];
 if (Array.isArray(target)) {
 target.splice(parseInt(lastPart), 1);
 }
 return newSteps;
}

function getDefaultConfig(type) {
 switch (type) {
 case 'delay': return { hours: 24, business_hours_only: false };
 case 'email': return { mode: 'template', template_id: null, custom_subject: '', signature: 'personal', ai_personalize: true };
 case 'task': return { title: '', assignee: 'owner', due_hours: 24, priority: 'normal', category: 'call' };
 case 'stage': return { target_stage: '', log_activity: true, note: '' };
 case 'field': return { field_name: '', operation: 'set', value: '' };
 case 'webhook': return { url: '', method: 'POST', headers: {}, body: '', timeout_seconds: 10, retry_count: 0 };
 case 'condition': return { rules: [{ field: '', op: '==', value: '' }], logic: 'AND' };
 default: return {};
 }
}

function countSteps(steps) {
 let count = 0;
 for (const step of (steps || [])) {
 count++;
 if (step.yes) count += countSteps(step.yes);
 if (step.no) count += countSteps(step.no);
 }
 return count;
}

function getStepSummary(step, t) {
 const c = step.config || {};
 switch (step.type) {
 case 'delay': return c.hours >= 24 ? `${Math.round(c.hours / 24)} ${t('workflowEditor.config.days')}` : `${c.hours || 0}h`;
 case 'email': return c.mode === 'template' ? `Template #${c.template_id || '?'}` : (c.custom_subject || 'Custom');
 case 'task': return c.title || t('workflowEditor.stepTypes.task');
 case 'stage': return `→ ${c.target_stage || '?'}`;
 case 'field': return `${c.field_name || '?'} ${c.operation || ''} ${c.value || ''}`;
 case 'webhook': return `${c.method || 'POST'} ${c.url || ''}`;
 case 'condition': return c.rules?.[0] ? `${c.rules[0].field || '?'} ${c.rules[0].op || ''} ${c.rules[0].value || ''}` : '';
 default: return '';
 }
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function WorkflowEditor() {
 const { id } = useParams();
 const navigate = useNavigate();
 const { t } = useLanguage();

 const [workflow, setWorkflow] = useState(null);
 const [steps, setSteps] = useState([]);
 const [selectedStepPath, setSelectedStepPath] = useState(null);
 const [variables, setVariables] = useState({});
 const [isDirty, setIsDirty] = useState(false);
 const [saving, setSaving] = useState(false);
 const [saveError, setSaveError] = useState(null);
 const [loading, setLoading] = useState(true);
 const [activeTab, setActiveTab] = useTabParam('editor');
 const [showTestModal, setShowTestModal] = useState(false);

 useEffect(() => {
 loadWorkflow();
 loadVariables();
 }, [id]);

 // Auto-Save Draft alle 30 Sekunden
 useEffect(() => {
 if (!isDirty || !workflow) return;
 const timer = setTimeout(async () => {
 try {
 await api(`/api/sales/workflow-builder/${id}/draft`, {
 method: 'PUT', body: { draft_steps: steps }
 });
 } catch (e) { console.error('Draft save failed:', e); }
 }, 30000);
 return () => clearTimeout(timer);
 }, [steps, isDirty, id, workflow]);

 async function loadWorkflow() {
 setLoading(true);
 try {
 const data = await api(`/api/sales/workflow-builder/${id}`);
 setWorkflow(data);
 setSteps(data.draft_steps || data.steps || []);
 } catch (e) {
 console.error('Failed to load workflow:', e);
 }
 setLoading(false);
 }

 async function loadVariables() {
 try {
 setVariables(await api('/api/sales/workflow-variables'));
 } catch (e) { console.error(e); }
 }

 async function handleSave() {
 setSaving(true);
 setSaveError(null);
 try {
 const updated = await api(`/api/sales/workflow-builder/${id}`, {
 method: 'PUT',
 body: {
 name: workflow.name,
 segment: workflow.segment,
 trigger_type: workflow.trigger_type,
 trigger_config: workflow.trigger_config,
 steps,
 }
 });
 setWorkflow(updated);
 setIsDirty(false);
 } catch (e) {
 console.error('Save failed:', e);
 setSaveError(e.message || 'Speichern fehlgeschlagen');
 }
 setSaving(false);
 }

 function handleUpdateStep(path, newConfig) {
 setSteps(prev => updateStepAtPath(prev, path, (step) => ({ ...step, config: { ...step.config, ...newConfig } })));
 setIsDirty(true);
 }

 function handleAddStep(afterPath, type) {
 setSteps(prev => insertStepAfterPath(prev, afterPath, type));
 setIsDirty(true);
 }

 function handleRemoveStep(path) {
 if (selectedStepPath === path) setSelectedStepPath(null);
 setSteps(prev => removeStepAtPath(prev, path));
 setIsDirty(true);
 }

 if (loading) {
 return (
 <div className="flex items-center justify-center h-screen bg-[var(--bg-page)]">
 <div className="text-[var(--text-tertiary)] text-sm">Loading...</div>
 </div>
 );
 }

 if (!workflow) {
 return (
 <div className="flex flex-col items-center justify-center h-screen bg-[var(--bg-page)]">
 <div className="text-[var(--text-secondary)] text-sm mb-3">Workflow nicht gefunden</div>
 <button onClick={() => navigate('/sales/workflows')} className="text-sm text-[#338edc] hover:underline">
 {t('workflowEditor.back')}
 </button>
 </div>
 );
 }

 const stepCount = countSteps(steps);

 return (
 <div className="flex flex-col h-screen bg-[var(--bg-page)]">
 {/* Top Bar */}
 <WorkflowTopBar
 workflow={workflow}
 onBack={() => navigate('/sales/workflows')}
 onSave={handleSave}
 saving={saving}
 isDirty={isDirty}
 stepCount={stepCount}
 onNameChange={(name) => { setWorkflow(w => ({ ...w, name })); setIsDirty(true); setSaveError(null); }}
 onTest={() => setShowTestModal(true)}
 saveError={saveError}
 t={t}
 />

 {/* Tab Bar */}
 <div className="flex items-center gap-1 px-6 py-1.5 bg-[var(--bg-card)] border-b border-[var(--border-card)]">
 {[
 { key: 'editor', label: t('workflowEditor.tabs.editor'), icon: Edit3 },
 { key: 'executions', label: t('workflowEditor.tabs.executions'), icon: Activity },
 ].map(tab => {
 const TabIcon = tab.icon;
 return (
 <button
 key={tab.key}
 onClick={() => setActiveTab(tab.key)}
 className={`flex items-center gap-1.5 px-4 py-2 text-[12px] font-medium rounded-lg transition-colors ${
 activeTab === tab.key
 ? 'text-[#338edc] bg-[#EBF4FD] dark:bg-[#338edc]/10'
 : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
 }`}
 >
 <TabIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
 {tab.label}
 </button>
 );
 })}
 </div>

 {/* Content based on active tab */}
 {activeTab === 'editor' ? (
 <div className="flex flex-1 overflow-hidden">
 {/* Left: Step Pipeline (60%) */}
 <div className="w-[60%] border-r border-[var(--border-card)] overflow-y-auto p-6">
 <TriggerBlock
 workflow={workflow}
 onChange={(triggerType, triggerConfig, segment) => {
 const patch = { trigger_type: triggerType, trigger_config: triggerConfig };
 if (segment !== undefined) patch.segment = segment;
 setWorkflow(w => ({ ...w, ...patch }));
 setIsDirty(true);
 }}
 t={t}
 />

 <StepList
 steps={steps}
 selectedPath={selectedStepPath}
 onSelectStep={setSelectedStepPath}
 onAddStep={handleAddStep}
 onRemoveStep={handleRemoveStep}
 t={t}
 />

 {/* Workflow End marker */}
 {steps.length > 0 && (
 <div className="flex justify-center mt-4">
 <div className="flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-[var(--border-card)] text-[var(--text-tertiary)] text-xs">
 <CheckSquare className="w-3.5 h-3.5" />
 {t('workflowEditor.workflowEnd')}
 </div>
 </div>
 )}
 </div>

 {/* Right: Config Panel (40%) */}
 <div className="w-[40%] bg-[var(--bg-inset)] overflow-y-auto p-6">
 {selectedStepPath ? (
 <StepConfigPanel
 step={getStepByPath(steps, selectedStepPath)}
 path={selectedStepPath}
 variables={variables}
 onUpdate={(newConfig) => handleUpdateStep(selectedStepPath, newConfig)}
 onClose={() => setSelectedStepPath(null)}
 t={t}
 />
 ) : (
 <EmptyConfigPanel t={t} />
 )}
 </div>
 </div>
 ) : (
 <ExecutionLog workflowId={id} t={t} />
 )}

 {/* Test Modal */}
 {showTestModal && (
 <TestRunModal
 workflowId={id}
 steps={steps}
 onClose={() => setShowTestModal(false)}
 t={t}
 />
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// TOP BAR
// ═══════════════════════════════════════════════════════════════

function WorkflowTopBar({ workflow, onBack, onSave, saving, isDirty, stepCount, onNameChange, onTest, saveError, t }) {
 return (
 <div className="flex flex-col bg-[var(--bg-card)] border-b border-[var(--border-card)]">
 <div className="flex items-center justify-between px-6 py-3">
 <div className="flex items-center gap-3">
 <button onClick={onBack} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
 <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
 </button>
 <input
 value={workflow?.name || ''}
 onChange={(e) => onNameChange(e.target.value)}
 placeholder={t('workflowEditor.namePlaceholder') || 'Workflow-Name...'}
 className="text-[15px] font-semibold bg-transparent border border-[var(--border-card)] hover:border-[var(--border-input)] focus:border-[#338edc] outline-none text-[var(--text-primary)] min-w-[200px] px-2 py-1 rounded-[8px] transition-colors cursor-text"
 />
 <span className="text-[11px] bg-[var(--bg-inset)] text-[var(--text-secondary)] px-2 py-0.5 rounded-lg">
 {stepCount} {stepCount === 1 ? t('workflowEditor.step') : t('workflowEditor.steps')}
 </span>
 {workflow?.is_active && (
 <span className="text-[11px] bg-green-50 text-green-600 px-2 py-0.5 rounded-lg font-medium dark:bg-green-900/20 dark:text-green-400">
 {t('workflowEditor.active')}
 </span>
 )}
 {isDirty && (
 <span className="text-[11px] text-amber-500 font-medium">● {t('workflowEditor.unsaved')}</span>
 )}
 </div>
 <div className="flex items-center gap-2">
 <button
 onClick={onTest}
 className="px-4 py-2 text-[12px] font-medium border border-[var(--border-card)] rounded-[10px] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)] transition-colors"
 >
 {t('workflowEditor.test')}
 </button>
 <button
 onClick={onSave}
 disabled={!isDirty || saving}
 className="flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold text-white bg-[#338edc] rounded-[10px] hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
 >
 <Save className="w-3.5 h-3.5" />
 {saving ? t('workflowEditor.saving') : t('workflowEditor.save')}
 </button>
 </div>
 </div>
 {saveError && (
 <div className="px-6 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-[12px] font-medium border-t border-red-100 dark:border-red-800/30">
 {t('workflowEditor.saveError') || 'Fehler beim Speichern'}: {saveError}
 </div>
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// TRIGGER BLOCK
// ═══════════════════════════════════════════════════════════════

// #1619 — Stop-Flags pro Sequenz (fehlendes Flag = true / AN)
const STOP_FLAG_KEYS = ['stop_on_reply', 'stop_on_booking', 'stop_on_stage_change', 'stop_on_won_lost'];

function TriggerBlock({ workflow, onChange, t }) {
 const triggerConfig = workflow?.trigger_config || {};

 function updateTrigger(field, value) {
 const newConfig = { ...triggerConfig, [field]: value };
 onChange(workflow?.trigger_type, newConfig);
 }

 return (
 <div className="p-4 rounded-xl border-2 border-dashed border-[#338edc] bg-[#EBF4FD] dark:bg-[#338edc]/10 mb-4">
 <div className="text-[11px] font-semibold text-[#338edc] uppercase tracking-wider mb-2">
 {t('workflowEditor.trigger')}
 </div>

 <select
 value={workflow?.trigger_type || 'stage_change'}
 onChange={(e) => {
 // #1619 — Stop-Flags beim Trigger-Typ-Wechsel erhalten (nur Trigger-Felder resetten)
 const kept = {};
 for (const k of STOP_FLAG_KEYS) {
 if (triggerConfig[k] !== undefined) kept[k] = triggerConfig[k];
 }
 onChange(e.target.value, kept);
 }}
 className="w-full p-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] mb-3"
 >
 {TRIGGERS.map(tr => (
 <option key={tr.id} value={tr.id}>{t(`workflowEditor.triggers.${tr.id}`)}</option>
 ))}
 </select>

 {workflow?.trigger_type === 'stage_change' && (
 <div className="flex gap-2 items-center">
 <select
 value={triggerConfig.from_stage || ''}
 onChange={(e) => updateTrigger('from_stage', e.target.value)}
 className="flex-1 p-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[12px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
 >
 <option value="">Von (beliebig)</option>
 {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
 </select>
 <span className="text-[var(--text-secondary)]">→</span>
 <select
 value={triggerConfig.to_stage || ''}
 onChange={(e) => updateTrigger('to_stage', e.target.value)}
 className="flex-1 p-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[12px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
 >
 <option value="">Nach (beliebig)</option>
 {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
 </select>
 </div>
 )}

 {workflow?.trigger_type === 'score_threshold' && (
 <div className="flex gap-2 items-center">
 <span className="text-[12px] text-[var(--text-secondary)]">Score</span>
 <select
 value={triggerConfig.direction || 'above'}
 onChange={(e) => updateTrigger('direction', e.target.value)}
 className="p-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[12px] focus:outline-none focus:border-[#338edc]"
 >
 <option value="above">≥</option>
 <option value="below">≤</option>
 </select>
 <input
 type="number"
 value={triggerConfig.min_score || ''}
 onChange={(e) => updateTrigger('min_score', parseInt(e.target.value) || 0)}
 className="w-20 p-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[12px] focus:outline-none focus:border-[#338edc]"
 />
 </div>
 )}

 {workflow?.trigger_type === 'no_activity' && (
 <div className="flex gap-2 items-center">
 <span className="text-[12px] text-[var(--text-secondary)]">Keine Aktivität seit</span>
 <input
 type="number"
 value={triggerConfig.days || ''}
 onChange={(e) => updateTrigger('days', parseInt(e.target.value) || 0)}
 className="w-20 p-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[12px] focus:outline-none focus:border-[#338edc]"
 />
 <span className="text-[12px] text-[var(--text-secondary)]">{t('workflowEditor.config.days')}</span>
 </div>
 )}

 {/* Segment-Filter */}
 <div className="mt-3">
 <div className="text-[11px] text-[var(--text-secondary)] mb-1">{t('sales.workflow_segment')}</div>
 <select
 value={workflow?.segment || ''}
 onChange={(e) => {
 const seg = e.target.value || '';
 onChange(workflow?.trigger_type, triggerConfig, seg);
 }}
 className="w-full p-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[12px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
 >
 <option value="">{t('sales.alle_segmente')}</option>
 <option value="startup">Start-Up</option>
 <option value="midscale">Mid-Scale</option>
 <option value="enterprise">Enterprise</option>
 </select>
 <div className="text-[10px] text-[var(--text-tertiary)] mt-1">{t('sales.workflow_segment_hint')}</div>
 </div>

 {/* #1619 — Stop-Bedingungen pro Sequenz (Default AN) */}
 <div className="mt-3 pt-3 border-t border-[#338edc]/20">
 <div className="text-[11px] text-[var(--text-secondary)] mb-2">{t('workflowEditor.stopFlags.title')}</div>
 <div className="flex flex-col gap-2">
 <ConfigToggle
 checked={triggerConfig.stop_on_reply !== false}
 onChange={(v) => updateTrigger('stop_on_reply', v)}
 label={t('workflowEditor.stopFlags.reply')}
 />
 <ConfigToggle
 checked={triggerConfig.stop_on_booking !== false}
 onChange={(v) => updateTrigger('stop_on_booking', v)}
 label={t('workflowEditor.stopFlags.booking')}
 />
 <ConfigToggle
 checked={triggerConfig.stop_on_stage_change !== false}
 onChange={(v) => updateTrigger('stop_on_stage_change', v)}
 label={t('workflowEditor.stopFlags.stageChange')}
 />
 <ConfigToggle
 checked={triggerConfig.stop_on_won_lost !== false}
 onChange={(v) => updateTrigger('stop_on_won_lost', v)}
 label={t('workflowEditor.stopFlags.wonLost')}
 />
 </div>
 <div className="text-[10px] text-[var(--text-tertiary)] mt-2">{t('workflowEditor.stopFlags.hint')}</div>
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// STEP LIST (Pipeline)
// ═══════════════════════════════════════════════════════════════

function StepList({ steps, selectedPath, onSelectStep, onAddStep, onRemoveStep, t }) {
 return (
 <div>
 {steps.map((step, index) => (
 <div key={step.id || index}>
 {/* Connector Line */}
 {index > 0 && (
 <div className="flex justify-center py-1">
 <div className="w-0.5 h-4 bg-[var(--border-card)]" />
 </div>
 )}

 <StepItem
 step={step}
 path={`${index}`}
 isSelected={selectedPath === `${index}`}
 onSelect={() => onSelectStep(`${index}`)}
 onRemove={() => onRemoveStep(`${index}`)}
 t={t}
 />

 {/* Condition branches */}
 {step.type === 'condition' && (
 <ConditionBranches
 step={step}
 basePath={`${index}`}
 selectedPath={selectedPath}
 onSelectStep={onSelectStep}
 onAddStep={onAddStep}
 onRemoveStep={onRemoveStep}
 t={t}
 />
 )}

 {/* Add-Button after each step */}
 <div className="flex justify-center py-1">
 <div className="w-0.5 h-3 bg-[var(--border-card)]" />
 </div>
 <AddStepButton onAdd={(type) => onAddStep(`${index}`, type)} t={t} />
 </div>
 ))}

 {steps.length === 0 && (
 <div className="flex justify-center py-4">
 <AddStepButton onAdd={(type) => onAddStep('-1', type)} isFirst t={t} />
 </div>
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// STEP ITEM
// ═══════════════════════════════════════════════════════════════

function StepItem({ step, path, isSelected, onSelect, onRemove, t }) {
 const type = STEP_TYPES[step.type];
 if (!type) return null;
 const Icon = type.icon;

 return (
 <div
 onClick={onSelect}
 className={`
 group flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all
 ${isSelected
 ? 'border-[#338edc] bg-[#EBF4FD] dark:bg-[#338edc]/10 ring-2 ring-[#338edc]/10'
 : 'border-[var(--border-card)] bg-[var(--bg-card)] hover:border-[var(--border-default,#E5E7EB)] dark:hover:border-[var(--border-default,#E5E7EB)]'}
 `}
 >
 {/* Drag Handle */}
 <div className="cursor-grab text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]">
 <GripVertical className="w-4 h-4" strokeWidth={1.5} />
 </div>

 {/* Step Icon */}
 <div
 className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
 style={{ background: `${type.color}15`, border: `1.5px solid ${type.color}40` }}
 >
 <Icon className="w-4 h-4" style={{ color: type.color }} strokeWidth={1.5} />
 </div>

 {/* Step Info */}
 <div className="flex-1 min-w-0">
 <div className="text-[13px] font-semibold text-[var(--text-primary)]">
 {t(`workflowEditor.stepTypes.${step.type}`)}
 </div>
 <div className="text-[11px] text-[var(--text-secondary)] truncate">
 {getStepSummary(step, t)}
 </div>
 </div>

 {/* Remove Button */}
 <button
 onClick={(e) => { e.stopPropagation(); onRemove(); }}
 className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity"
 >
 <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
 </button>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// CONDITION BRANCHES
// ═══════════════════════════════════════════════════════════════

function ConditionBranches({ step, basePath, selectedPath, onSelectStep, onAddStep, onRemoveStep, t }) {
 return (
 <div className="flex gap-3 mt-2 ml-4">
 {/* YES Branch */}
 <div className="flex-1 border-l-[3px] border-green-500 pl-3 rounded-bl-lg">
 <div className="text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-wider mb-2">
 ✓ {t('workflowEditor.yesBranch')}
 </div>
 {(step.yes || []).map((s, i) => (
 <div key={s.id || i}>
 {i > 0 && (
 <div className="flex justify-center py-1">
 <div className="w-0.5 h-3 bg-green-200 dark:bg-green-800" />
 </div>
 )}
 <StepItem
 step={s}
 path={`${basePath}.yes.${i}`}
 isSelected={selectedPath === `${basePath}.yes.${i}`}
 onSelect={() => onSelectStep(`${basePath}.yes.${i}`)}
 onRemove={() => onRemoveStep(`${basePath}.yes.${i}`)}
 t={t}
 />
 {s.type === 'condition' && (
 <ConditionBranches
 step={s} basePath={`${basePath}.yes.${i}`}
 selectedPath={selectedPath} onSelectStep={onSelectStep}
 onAddStep={onAddStep} onRemoveStep={onRemoveStep} t={t}
 />
 )}
 </div>
 ))}
 <div className="mt-2">
 <AddStepButton
 small
 onAdd={(type) => onAddStep(`${basePath}.yes.${Math.max((step.yes || []).length - 1, -1)}`, type)}
 t={t}
 />
 </div>
 </div>

 {/* NO Branch */}
 <div className="flex-1 border-l-[3px] border-red-500 pl-3 rounded-bl-lg">
 <div className="text-[10px] font-bold text-red-600 dark:text-red-400 uppercase tracking-wider mb-2">
 ✗ {t('workflowEditor.noBranch')}
 </div>
 {(step.no || []).map((s, i) => (
 <div key={s.id || i}>
 {i > 0 && (
 <div className="flex justify-center py-1">
 <div className="w-0.5 h-3 bg-red-200 dark:bg-red-800" />
 </div>
 )}
 <StepItem
 step={s}
 path={`${basePath}.no.${i}`}
 isSelected={selectedPath === `${basePath}.no.${i}`}
 onSelect={() => onSelectStep(`${basePath}.no.${i}`)}
 onRemove={() => onRemoveStep(`${basePath}.no.${i}`)}
 t={t}
 />
 {s.type === 'condition' && (
 <ConditionBranches
 step={s} basePath={`${basePath}.no.${i}`}
 selectedPath={selectedPath} onSelectStep={onSelectStep}
 onAddStep={onAddStep} onRemoveStep={onRemoveStep} t={t}
 />
 )}
 </div>
 ))}
 <div className="mt-2">
 <AddStepButton
 small
 onAdd={(type) => onAddStep(`${basePath}.no.${Math.max((step.no || []).length - 1, -1)}`, type)}
 t={t}
 />
 </div>
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// ADD STEP BUTTON
// ═══════════════════════════════════════════════════════════════

function AddStepButton({ onAdd, small, isFirst, t }) {
 const [open, setOpen] = useState(false);

 return (
 <div className="relative">
 <button
 onClick={() => setOpen(!open)}
 className={`
 ${small ? 'w-full py-1.5 text-[11px]' : 'w-full py-2 text-[12px]'}
 border-2 border-dashed border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)] rounded-lg text-[var(--text-tertiary)]
 hover:border-[#338edc] hover:text-[#338edc] hover:bg-[#EBF4FD] dark:hover:bg-[#338edc]/10 transition-all
 `}
 >
 {isFirst ? `+ ${t('workflowEditor.addFirstStep')}` : '+'}
 </button>

 {open && (
 <>
 <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
 <div className="absolute left-1/2 -translate-x-1/2 mt-1 z-20 bg-[var(--bg-card)] rounded-xl border border-[var(--border-card)] shadow-[0_4px_24px_rgba(0,0,0,0.08)] p-2 min-w-[220px]">
 {Object.entries(STEP_TYPES).map(([key, val]) => {
 const Icon = val.icon;
 return (
 <button
 key={key}
 onClick={() => { onAdd(key); setOpen(false); }}
 className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] rounded-lg hover:bg-[var(--bg-inset)] text-left text-[var(--text-primary)] transition-colors"
 >
 <Icon className="w-4 h-4 flex-shrink-0" style={{ color: val.color }} strokeWidth={1.5} />
 {t(`workflowEditor.stepTypes.${key}`)}
 </button>
 );
 })}
 </div>
 </>
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// CONFIG PANEL
// ═══════════════════════════════════════════════════════════════

function StepConfigPanel({ step, path, variables, onUpdate, onClose, t }) {
 if (!step) return <EmptyConfigPanel t={t} />;
 const type = STEP_TYPES[step.type];
 if (!type) return null;
 const Icon = type.icon;

 return (
 <div>
 <div className="flex items-center justify-between mb-5">
 <div className="flex items-center gap-2">
 <div
 className="w-8 h-8 rounded-lg flex items-center justify-center"
 style={{ background: `${type.color}15`, border: `1.5px solid ${type.color}40` }}
 >
 <Icon className="w-4 h-4" style={{ color: type.color }} strokeWidth={1.5} />
 </div>
 <span className="font-semibold text-[13px] text-[var(--text-primary)]">
 {t(`workflowEditor.stepTypes.${step.type}`)}
 </span>
 </div>
 <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors">
 <X className="w-4 h-4" strokeWidth={1.5} />
 </button>
 </div>

 {step.type === 'delay' && <DelayConfig config={step.config || {}} onUpdate={onUpdate} t={t} />}
 {step.type === 'email' && <EmailConfig config={step.config || {}} variables={variables || {}} onUpdate={onUpdate} t={t} />}
 {step.type === 'task' && <TaskConfig config={step.config || {}} variables={variables || {}} onUpdate={onUpdate} t={t} />}
 {step.type === 'stage' && <StageConfig config={step.config || {}} onUpdate={onUpdate} t={t} />}
 {step.type === 'field' && <FieldConfig config={step.config || {}} onUpdate={onUpdate} t={t} />}
 {step.type === 'webhook' && <WebhookConfig config={step.config || {}} variables={variables || {}} onUpdate={onUpdate} t={t} />}
 {step.type === 'condition' && <ConditionConfig config={step.config || {}} onUpdate={onUpdate} t={t} />}
 </div>
 );
}

function EmptyConfigPanel({ t }) {
 return (
 <div className="flex flex-col items-center justify-center h-full text-center">
 <MousePointer className="w-10 h-10 text-[var(--text-tertiary)] mb-3" strokeWidth={1} />
 <div className="text-[13px] font-medium text-[var(--text-secondary)]">
 {t('workflowEditor.selectStep')}
 </div>
 <div className="text-[11px] text-[var(--text-tertiary)] mt-1">
 {t('workflowEditor.selectStepDesc')}
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// CONFIG: Label helper
// ═══════════════════════════════════════════════════════════════

function ConfigLabel({ children }) {
 return <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1 uppercase">{children}</label>;
}

function ConfigInput({ value, onChange, type = 'text', placeholder, className = '' }) {
 return (
 <input
 type={type}
 value={value}
 onChange={onChange}
 placeholder={placeholder}
 className={`w-full px-3 py-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] ${className}`}
 />
 );
}

function ConfigSelect({ value, onChange, children, className = '' }) {
 return (
 <select
 value={value}
 onChange={onChange}
 className={`w-full px-3 py-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] ${className}`}
 >
 {children}
 </select>
 );
}

function ConfigToggle({ checked, onChange, label }) {
 return (
 <label className="flex items-center gap-2 cursor-pointer">
 <ToggleSwitch checked={checked} onChange={onChange} />
 <span className="text-[12px] text-[var(--text-secondary)]">{label}</span>
 </label>
 );
}

// ═══════════════════════════════════════════════════════════════
// CONFIG: Delay
// ═══════════════════════════════════════════════════════════════

function DelayConfig({ config, onUpdate, t }) {
 const [unit, setUnit] = useState(config.hours >= 24 && config.hours % 24 === 0 ? 'days' : 'hours');
 const displayValue = unit === 'days' ? (config.hours || 0) / 24 : (config.hours || 0);

 function handleValueChange(val) {
 const num = parseFloat(val) || 0;
 onUpdate({ hours: unit === 'days' ? num * 24 : num });
 }

 function handleUnitChange(newUnit) {
 setUnit(newUnit);
 const hours = config.hours || 0;
 if (newUnit === 'days') {
 onUpdate({ hours: Math.round(hours / 24) * 24 || 24 });
 }
 }

 return (
 <div className="space-y-4">
 <div>
 <ConfigLabel>{t('workflowEditor.stepTypes.delay')}</ConfigLabel>
 <div className="flex gap-2">
 <ConfigInput
 type="number"
 value={displayValue}
 onChange={(e) => handleValueChange(e.target.value)}
 className="flex-1"
 />
 <ConfigSelect
 value={unit}
 onChange={(e) => handleUnitChange(e.target.value)}
 className="w-28"
 >
 <option value="minutes">{t('workflowEditor.config.minutes')}</option>
 <option value="hours">{t('workflowEditor.config.hours')}</option>
 <option value="days">{t('workflowEditor.config.days')}</option>
 </ConfigSelect>
 </div>
 </div>

 <ConfigToggle
 checked={config.business_hours_only || false}
 onChange={(v) => onUpdate({ business_hours_only: v })}
 label={t('workflowEditor.config.businessHoursOnly')}
 />
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// VARIABLE INPUT (single-line with auto-complete)
// ═══════════════════════════════════════════════════════════════

function flattenVariables(variables) {
 return Object.entries(variables || {}).flatMap(([group, vars]) =>
 (Array.isArray(vars) ? vars : []).map(v => ({ ...v, group }))
 );
}

function VariableInput({ value, onChange, variables, placeholder, t }) {
 const [showSuggestions, setShowSuggestions] = useState(false);
 const [filter, setFilter] = useState('');
 const inputRef = useRef(null);
 const allVars = flattenVariables(variables);

 const filtered = allVars.filter(v =>
 !filter || v.key.toLowerCase().includes(filter.toLowerCase()) || v.label.toLowerCase().includes(filter.toLowerCase())
 );

 function handleInput(e) {
 const newValue = e.target.value;
 onChange(newValue);
 const pos = e.target.selectionStart;
 const textBefore = newValue.substring(0, pos);
 const match = textBefore.match(/\{\{([\w.]*)?$/);
 if (match) {
 setFilter(match[1] || '');
 setShowSuggestions(true);
 } else {
 setShowSuggestions(false);
 }
 }

 function insertVariable(varKey) {
 const input = inputRef.current;
 const pos = input.selectionStart;
 const textBefore = value.substring(0, pos);
 const textAfter = value.substring(pos);
 const match = textBefore.match(/\{\{([\w.]*)?$/);
 if (match) {
 const start = textBefore.length - match[0].length;
 const newValue = textBefore.substring(0, start) + `{{${varKey}}}` + textAfter;
 onChange(newValue);
 } else {
 onChange(value + `{{${varKey}}}`);
 }
 setShowSuggestions(false);
 setTimeout(() => input?.focus(), 0);
 }

 return (
 <div className="relative">
 <input
 ref={inputRef}
 value={value}
 onChange={handleInput}
 placeholder={placeholder}
 className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
 />

 {showSuggestions && filtered.length > 0 && (
 <VariableSuggestionDropdown
 filtered={filtered}
 onInsert={insertVariable}
 onClose={() => setShowSuggestions(false)}
 />
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// VARIABLE SUGGESTION DROPDOWN (shared)
// ═══════════════════════════════════════════════════════════════

function VariableSuggestionDropdown({ filtered, onInsert, onClose }) {
 return (
 <>
 <div className="fixed inset-0 z-10" onClick={onClose} />
 <div className="absolute left-0 right-0 mt-1 z-20 bg-[var(--bg-card)] rounded-xl border border-[var(--border-card)] shadow-[0_4px_24px_rgba(0,0,0,0.08)] max-h-48 overflow-y-auto">
 {['lead', 'user', 'system'].map(group => {
 const groupVars = filtered.filter(v => v.group === group);
 if (groupVars.length === 0) return null;
 return (
 <div key={group}>
 <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] bg-[var(--bg-inset)] sticky top-0">
 {group}
 </div>
 {groupVars.map(v => (
 <button
 key={v.key}
 onClick={() => onInsert(v.key)}
 className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[#EBF4FD] dark:hover:bg-[#338edc]/10 text-[12px] transition-colors"
 >
 <span className="flex items-center gap-2">
 <code className="text-[10px] bg-[#D6E9FA] dark:bg-[#338edc]/20 text-[#338edc] dark:text-[#66ABE5] px-1.5 py-0.5 rounded">
 {`{{${v.key}}}`}
 </code>
 <span className="text-[var(--text-secondary)] text-[11px]">{v.label}</span>
 </span>
 <span className="text-[10px] text-[var(--text-tertiary)]">{v.example}</span>
 </button>
 ))}
 </div>
 );
 })}
 </div>
 </>
 );
}

// ═══════════════════════════════════════════════════════════════
// VARIABLE RICH EDITOR (Tiptap with toolbar + variable picker)
// ═══════════════════════════════════════════════════════════════

function VariableRichEditor({ value, onChange, variables, placeholder }) {
 const [showVarPicker, setShowVarPicker] = useState(false);
 const [showPreview, setShowPreview] = useState(false);
 const textareaRef = useRef(null);
 const allVars = flattenVariables(variables);

 function insertVariable(varKey) {
 const ta = textareaRef.current;
 if (!ta) return;
 const insert = `{{${varKey}}}`;
 const start = ta.selectionStart;
 const end = ta.selectionEnd;
 const newVal = value.substring(0, start) + insert + value.substring(end);
 onChange(newVal);
 setShowVarPicker(false);
 // Cursor nach dem eingefügten Text positionieren
 setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + insert.length; }, 0);
 }

 return (
 <div className="border border-[var(--border-input)] rounded-[10px] overflow-hidden focus-within:border-[#338edc]">
 {/* Toolbar */}
 <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-[var(--border-card)] bg-[var(--bg-inset)]">
 {/* Variable Picker */}
 <div className="relative">
 <button
 onClick={() => setShowVarPicker(!showVarPicker)}
 className="px-2 py-1 text-[11px] rounded-lg bg-[#D6E9FA] dark:bg-[#338edc]/20 text-[#338edc] dark:text-[#66ABE5] hover:bg-[#B8D8F5] dark:hover:bg-[#338edc]/30 font-medium transition-colors"
 >
 {'{{ }}'} Variable
 </button>

 {showVarPicker && (
 <>
 <div className="fixed inset-0 z-10" onClick={() => setShowVarPicker(false)} />
 <div className="absolute left-0 mt-1 z-20 bg-[var(--bg-card)] rounded-xl border border-[var(--border-card)] shadow-[0_4px_24px_rgba(0,0,0,0.08)] w-72 max-h-60 overflow-y-auto">
 {['lead', 'user', 'system'].map(group => {
 const groupVars = allVars.filter(v => v.group === group);
 if (groupVars.length === 0) return null;
 return (
 <div key={group}>
 <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] bg-[var(--bg-inset)] sticky top-0">
 {group}
 </div>
 {groupVars.map(v => (
 <button
 key={v.key}
 onClick={() => insertVariable(v.key)}
 className="w-full flex items-center justify-between px-3 py-1.5 text-left hover:bg-[#EBF4FD] dark:hover:bg-[#338edc]/10 text-[11px] transition-colors"
 >
 <code className="bg-[#D6E9FA] dark:bg-[#338edc]/20 text-[#338edc] dark:text-[#66ABE5] px-1.5 py-0.5 rounded text-[10px]">
 {`{{${v.key}}}`}
 </code>
 <span className="text-[var(--text-tertiary)] text-[10px]">{v.example}</span>
 </button>
 ))}
 </div>
 );
 })}
 </div>
 </>
 )}
 </div>

 <div className="flex-1" />

 {/* Preview Toggle */}
 <button
 onClick={() => setShowPreview(!showPreview)}
 className={`px-2 py-1 text-[11px] rounded-lg font-medium transition-colors ${
 showPreview
 ? 'bg-[#338edc] text-white'
 : 'text-[var(--text-secondary)] hover:bg-[var(--bg-card)]'
 }`}
 >
 {showPreview ? 'Code' : 'Preview'}
 </button>
 </div>

 {/* Code Textarea or Preview */}
 {showPreview ? (
 <div
 className="p-3 min-h-[120px] text-[13px] text-[var(--text-primary)] bg-[var(--bg-card)] prose prose-sm max-w-none"
 dangerouslySetInnerHTML={{ __html: value || `<p style="color:var(--text-tertiary)">${placeholder || ''}</p>` }}
 />
 ) : (
 <textarea
 ref={textareaRef}
 value={value || ''}
 onChange={(e) => onChange(e.target.value)}
 rows={8}
 placeholder={placeholder}
 className="w-full p-3 min-h-[120px] text-[12px] text-[var(--text-primary)] bg-[var(--bg-card)] font-mono resize-y outline-none placeholder:text-[var(--text-tertiary)]"
 spellCheck={false}
 />
 )}
 </div>
 );
}

function TiptapToolbarButton({ onClick, active, icon }) {
 return (
 <button
 onClick={onClick}
 type="button"
 className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
 active ? 'bg-[#338edc]/10 text-[#338edc]' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)]'
 }`}
 >
 {icon}
 </button>
 );
}

// ═══════════════════════════════════════════════════════════════
// CONFIG: Email (Full inline builder)
// ═══════════════════════════════════════════════════════════════

function EmailConfig({ config, variables, onUpdate, t }) {
 const [templates, setTemplates] = useState([]);
 const [assets, setAssets] = useState([]);
 const [leads, setLeads] = useState([]);
 const [previewLead, setPreviewLead] = useState(null);
 const [previewData, setPreviewData] = useState(null);
 const [showPreview, setShowPreview] = useState(false);
 const [previewLoading, setPreviewLoading] = useState(false);
 const [templateBody, setTemplateBody] = useState(null); // {subject, body} from template
 const [templateBodyLoading, setTemplateBodyLoading] = useState(false);
 const [showTemplatePreview, setShowTemplatePreview] = useState(false);
 const [testSending, setTestSending] = useState(false);
 const [testResult, setTestResult] = useState(null);

 useEffect(() => {
 api('/api/email/templates').then(data => {
 const all = Array.isArray(data) ? data : data?.templates || [];
 setTemplates(all.filter(t => t.aktiv !== false));
 }).catch(() => {});
 api('/api/sales/assets').then(data => setAssets(Array.isArray(data) ? data : [])).catch(() => {});
 api('/api/leads?limit=10').then(data => setLeads(data?.leads || [])).catch(() => {});
 }, []);

 // Auto-load template body when template is selected
 useEffect(() => {
 if (config.mode === 'template' && config.template_id) {
 setTemplateBodyLoading(true);
 api(`/api/sales/workflow-template-body/${config.template_id}`)
 .then(data => setTemplateBody(data))
 .catch(() => setTemplateBody(null))
 .finally(() => setTemplateBodyLoading(false));
 } else {
 setTemplateBody(null);
 }
 }, [config.mode, config.template_id]);

 async function loadPreview() {
 if (!previewLead) return;
 setPreviewLoading(true);
 try {
 const data = await api('/api/sales/workflow-preview-email', {
 method: 'POST',
 body: {
 lead_id: previewLead,
 subject: config.custom_subject || '',
 body: config.custom_body || '',
 template_id: config.mode === 'template' ? config.template_id : null,
 signature: config.signature || 'personal',
 ai_personalize: config.ai_personalize !== false,
 }
 });
 setPreviewData(data);
 setShowPreview(true);
 } catch (e) { console.error('Preview failed:', e); }
 setPreviewLoading(false);
 }

 async function handleTestSend() {
 if (!previewLead) return;
 setTestSending(true);
 setTestResult(null);
 try {
 const data = await api('/api/sales/workflow-step/test-send', {
 method: 'POST',
 body: {
 lead_id: previewLead,
 mode: config.mode || 'template',
 template_id: config.mode === 'template' ? config.template_id : null,
 custom_subject: config.custom_subject || '',
 custom_body: config.custom_body || '',
 ai_personalize: config.ai_personalize !== false,
 signature: config.signature || 'personal',
 }
 });
 if (data.success) {
 setTestResult({ type: 'success', message: `${t('workflowEditor.config.testMailSuccess')}: ${data.sent_to}` });
 } else {
 setTestResult({ type: 'error', message: data.error || t('workflowEditor.config.testMailError') });
 }
 } catch (e) {
 setTestResult({ type: 'error', message: e.message || t('workflowEditor.config.testMailError') });
 }
 setTestSending(false);
 // Auto-hide nach 6 Sekunden
 setTimeout(() => setTestResult(null), 6000);
 }

 return (
 <>
 <div className="space-y-4">
 {/* Mode Toggle */}
 <div>
 <ConfigLabel>{t('workflowEditor.config.emailMode')}</ConfigLabel>
 <div className="flex gap-1 bg-[var(--bg-inset)] rounded-[10px] p-1 border border-[var(--border-card)]">
 <button
 onClick={() => onUpdate({ mode: 'template' })}
 className={`flex-1 py-1.5 text-[12px] font-medium rounded-[8px] transition-all ${
 config.mode === 'template' ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)]'
 }`}
 >
 {t('workflowEditor.config.template')}
 </button>
 <button
 onClick={() => onUpdate({ mode: 'custom' })}
 className={`flex-1 py-1.5 text-[12px] font-medium rounded-[8px] transition-all ${
 config.mode === 'custom' ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)]'
 }`}
 >
 {t('workflowEditor.config.customEmail')}
 </button>
 </div>
 </div>

 {/* Template Selection */}
 {config.mode === 'template' && (
 <div>
 <ConfigLabel>{t('workflowEditor.config.template')}</ConfigLabel>
 <ConfigSelect
 value={config.template_id || ''}
 onChange={(e) => onUpdate({ template_id: parseInt(e.target.value) || null })}
 >
 <option value="">— {t('workflowEditor.config.selectTemplate')} —</option>
 {templates.map(tpl => (
 <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
 ))}
 </ConfigSelect>
 </div>
 )}

 {/* Template Body Preview (button → modal) */}
 {config.mode === 'template' && config.template_id && (
 <div>
 <ConfigLabel>{t('workflowEditor.config.templatePreview')}</ConfigLabel>
 {templateBodyLoading ? (
 <div className="p-3 border border-[var(--border-card)] rounded-[10px] bg-[var(--bg-inset)] text-[11px] text-[var(--text-tertiary)]">
 {t('common.loading')}...
 </div>
 ) : templateBody?.body ? (
 <button
 onClick={() => setShowTemplatePreview(true)}
 className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium border border-[var(--border-card)] bg-[var(--bg-card)] text-[var(--text-primary)] rounded-[8px] hover:bg-[var(--bg-inset)] transition-colors"
 >
 <Eye className="w-3 h-3" strokeWidth={1.5} />
 {t('workflowEditor.config.preview')}
 {templateBody.subject && <span className="text-[var(--text-tertiary)] ml-1 truncate max-w-[180px]">— {templateBody.subject}</span>}
 </button>
 ) : (
 <div className="p-3 border border-[var(--border-card)] rounded-[10px] bg-[var(--bg-inset)] text-[11px] text-[var(--text-tertiary)]">
 {t('workflowEditor.config.noTemplateBody')}
 </div>
 )}
 </div>
 )}

 {/* Subject (always shown) */}
 <div>
 <ConfigLabel>{t('workflowEditor.config.subject')}</ConfigLabel>
 <VariableInput
 value={config.custom_subject || ''}
 onChange={(val) => onUpdate({ custom_subject: val })}
 variables={variables}
 placeholder={`z.B. ${t('workflowEditor.config.subject')}... {{lead.name}}`}
 t={t}
 />
 </div>

 {/* Body (Custom mode only) */}
 {config.mode === 'custom' && (
 <div>
 <ConfigLabel>{t('workflowEditor.config.emailText')}</ConfigLabel>
 <VariableRichEditor
 value={config.custom_body || ''}
 onChange={(val) => onUpdate({ custom_body: val })}
 variables={variables}
 placeholder={t('workflowEditor.config.emailText') + '...'}
 />
 </div>
 )}

 {/* AI Personalization */}
 <div>
 <label className="flex items-center gap-2 text-[12px] text-[var(--text-secondary)] cursor-pointer">
 <input
 type="checkbox"
 checked={config.ai_personalize !== false}
 onChange={(e) => onUpdate({ ai_personalize: e.target.checked })}
 className="rounded border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)] text-[#338edc] focus:ring-[#338edc] w-3.5 h-3.5"
 />
 {t('workflowEditor.config.aiPersonalize')}
 </label>
 {config.ai_personalize !== false && (
 <div className="mt-2 ml-5 p-2.5 bg-[#EBF4FD] dark:bg-[#338edc]/10 border border-[#D6E9FA] dark:border-[#338edc]/20 rounded-[8px]">
 <div className="flex items-start gap-2">
 <Sparkles className="w-3.5 h-3.5 text-[#338edc] mt-0.5 shrink-0" strokeWidth={1.5} />
 <div className="text-[10px] text-[var(--text-secondary)] space-y-1">
 <p className="font-medium text-[#338edc]">{t('workflowEditor.config.aiInfoTitle')}</p>
 <p>{t('workflowEditor.config.aiInfoText1')}</p>
 <p>{t('workflowEditor.config.aiInfoText2')}</p>
 <p className="text-[var(--text-tertiary)]">{t('workflowEditor.config.aiInfoFallback')}</p>
 </div>
 </div>
 </div>
 )}
 </div>

 {/* Signature */}
 <div>
 <ConfigLabel>{t('workflowEditor.config.signature')}</ConfigLabel>
 <ConfigSelect
 value={config.signature || 'personal'}
 onChange={(e) => onUpdate({ signature: e.target.value })}
 >
 <option value="personal">{t('workflowEditor.config.personalSignature')}</option>
 <option value="global">{t('workflowEditor.config.globalSignature')}</option>
 <option value="generic">{t('workflowEditor.config.genericSignature')}</option>
 </ConfigSelect>
 </div>

 {/* Attachments */}
 <div>
 <ConfigLabel>{t('workflowEditor.config.attachments')}</ConfigLabel>
 <div className="flex flex-wrap gap-1.5">
 {(config.attachments || []).map(assetId => {
 const asset = assets.find(a => a.id === assetId);
 return (
 <span key={assetId} className="inline-flex items-center gap-1 px-2 py-1 bg-[#EBF4FD] dark:bg-[#338edc]/15 text-[#338edc] dark:text-[#66ABE5] text-[11px] rounded-lg">
 <Paperclip className="w-3 h-3" strokeWidth={1.5} />
 {asset?.name || `Asset #${assetId}`}
 <button
 onClick={() => onUpdate({ attachments: (config.attachments || []).filter(a => a !== assetId) })}
 className="text-[#338edc]/50 hover:text-[#338edc] dark:text-[#66ABE5]/50 dark:hover:text-[#66ABE5] ml-0.5"
 >
 <X className="w-3 h-3" />
 </button>
 </span>
 );
 })}
 {assets.length > 0 && (
 <select
 value=""
 onChange={(e) => {
 if (e.target.value) {
 onUpdate({ attachments: [...(config.attachments || []), parseInt(e.target.value)] });
 }
 }}
 className="px-2 py-1 text-[11px] rounded-lg border border-dashed border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)] text-[var(--text-tertiary)] bg-transparent cursor-pointer"
 >
 <option value="">+ {t('workflowEditor.config.addAttachment')}</option>
 {assets.filter(a => !(config.attachments || []).includes(a.id)).map(a => (
 <option key={a.id} value={a.id}>{a.name}</option>
 ))}
 </select>
 )}
 </div>
 </div>

 {/* Live Preview with AI */}
 <div className="pt-3 border-t border-[var(--border-card)]">
 <ConfigLabel>{t('workflowEditor.config.livePreview')}</ConfigLabel>
 <div className="flex items-center gap-2 mb-2">
 <select
 value={previewLead || ''}
 onChange={(e) => setPreviewLead(parseInt(e.target.value) || null)}
 className="flex-1 px-2 py-1.5 text-[11px] rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
 >
 <option value="">{t('workflowEditor.config.selectLead')}</option>
 {leads.map(l => (
 <option key={l.id} value={l.id}>
 {l.ansprechpartner || l.firma || `Lead #${l.id}`}
 {l.firma ? ` (${l.firma})` : ''}
 </option>
 ))}
 </select>
 <button
 onClick={loadPreview}
 disabled={!previewLead || previewLoading}
 className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium border border-[var(--border-card)] bg-[var(--bg-card)] text-[var(--text-primary)] rounded-[8px] hover:bg-[var(--bg-inset)] disabled:opacity-50 transition-colors"
 >
 <Eye className="w-3 h-3" strokeWidth={1.5} />
 {previewLoading ? '...' : t('workflowEditor.config.preview')}
 </button>
 <button
 onClick={handleTestSend}
 disabled={!previewLead || testSending}
 className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium bg-[#338edc] text-white rounded-[8px] hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
 >
 {testSending ? (
 <>
 <span className="w-3 h-3 border-[1.5px] border-white border-t-transparent rounded-full animate-spin" />
 {t('workflowEditor.config.sending')}
 </>
 ) : (
 <>
 <Send className="w-3 h-3" strokeWidth={1.5} />
 {t('workflowEditor.config.sendTestMail')}
 </>
 )}
 </button>
 </div>

 {/* Test result feedback */}
 {testResult && (
 <div className={`mb-2 px-3 py-2 rounded-[8px] text-[11px] flex items-center gap-1.5 ${
 testResult.type === 'success'
 ? 'bg-[#F0FDF4] dark:bg-[#10B981]/10 text-[#15803D] dark:text-[#4ADE80] border border-[#DCFCE7] dark:border-[#10B981]/20'
 : 'bg-[#FEF2F2] dark:bg-[#EF4444]/10 text-[#B91C1C] dark:text-[#FCA5A5] border border-[#FEE2E2] dark:border-[#EF4444]/20'
 }`}>
 {testResult.type === 'success' ? <CheckCircle className="w-3 h-3 shrink-0" strokeWidth={1.5} /> : <XCircle className="w-3 h-3 shrink-0" strokeWidth={1.5} />}
 {testResult.message}
 </div>
 )}

 </div>
 </div>

 {/* Live Preview Modal — portaled to body */}
 {showPreview && previewData && createPortal(
 <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
 <div className="absolute inset-0 bg-black/60" onClick={() => setShowPreview(false)} />
 <div className="relative bg-white rounded-card w-full max-w-[700px] h-[80vh] shadow-modal animate-slide-in flex flex-col">
 <div className="flex items-center justify-between px-4 py-3 border-b border-line">
 <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{t('sales.workflow.emailPreview')}</span>
 <button onClick={() => setShowPreview(false)} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-secondary,#4B5563)] text-lg leading-none">&times;</button>
 </div>
 {previewData.ai_greeting && (
 <div className="px-4 py-2 bg-[#EBF4FD] border-b border-line flex items-center gap-1.5">
 <Zap className="w-3.5 h-3.5 text-[#338edc]" strokeWidth={1.5} />
 <span className="text-xs font-medium text-[#338edc]">{t('workflowEditor.config.aiPersonalized')}</span>
 </div>
 )}
 {previewData.subject && (
 <div className="px-4 py-2 bg-[var(--bg-inset,#F8F9FC)] border-b border-line">
 <span className="text-[10px] font-medium text-[var(--text-tertiary,#6B7280)] uppercase tracking-wide">{t('workflowEditor.config.subject')}</span>
 <div className="text-sm font-medium text-[var(--text-primary,#1A1A2E)] mt-0.5" dangerouslySetInnerHTML={{ __html: previewData.subject }} />
 </div>
 )}
 <div className="flex-1 overflow-hidden">
 <iframe srcDoc={previewData.body} title="Email Preview" className="w-full h-full border-0" sandbox="allow-same-origin" />
 </div>
 </div>
 </div>,
 document.body
 )}

 {/* Template Preview Modal — portaled to body */}
 {showTemplatePreview && templateBody?.body && createPortal(
 <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
 <div className="absolute inset-0 bg-black/60" onClick={() => setShowTemplatePreview(false)} />
 <div className="relative bg-white rounded-card w-full max-w-[700px] h-[80vh] shadow-modal animate-slide-in flex flex-col">
 <div className="flex items-center justify-between px-4 py-3 border-b border-line">
 <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{t('sales.workflow.emailPreview')}</span>
 <button onClick={() => setShowTemplatePreview(false)} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-secondary,#4B5563)] text-lg leading-none">&times;</button>
 </div>
 {templateBody.subject && (
 <div className="px-4 py-2 bg-[var(--bg-inset,#F8F9FC)] border-b border-line">
 <span className="text-[10px] font-medium text-[var(--text-tertiary,#6B7280)] uppercase tracking-wide">{t('workflowEditor.config.subject')}</span>
 <div className="text-sm font-medium text-[var(--text-primary,#1A1A2E)] mt-0.5">{templateBody.subject}</div>
 </div>
 )}
 <div className="flex-1 overflow-hidden">
 <iframe srcDoc={templateBody.body} title="Email Preview" className="w-full h-full border-0" sandbox="allow-same-origin" />
 </div>
 </div>
 </div>,
 document.body
 )}
 </>
 );
}

// ═══════════════════════════════════════════════════════════════
// CONFIG: Task
// ═══════════════════════════════════════════════════════════════

function TaskConfig({ config, variables, onUpdate, t }) {
 return (
 <div className="space-y-4">
 <div>
 <ConfigLabel>{t('workflowEditor.config.taskTitle')}</ConfigLabel>
 <ConfigInput
 value={config.title || ''}
 onChange={(e) => onUpdate({ title: e.target.value })}
 placeholder="Aufgaben-Titel..."
 />
 </div>

 <div>
 <ConfigLabel>{t('workflowEditor.config.assignee')}</ConfigLabel>
 <ConfigSelect
 value={config.assignee || 'owner'}
 onChange={(e) => onUpdate({ assignee: e.target.value })}
 >
 <option value="owner">{t('workflowEditor.config.leadOwner')}</option>
 <option value="round_robin">{t('workflowEditor.config.roundRobin')}</option>
 </ConfigSelect>
 </div>

 <div>
 <ConfigLabel>{t('workflowEditor.config.dueIn')}</ConfigLabel>
 <div className="flex items-center gap-2">
 <ConfigInput
 type="number"
 value={config.due_hours || 24}
 onChange={(e) => onUpdate({ due_hours: parseInt(e.target.value) || 24 })}
 className="w-24"
 />
 <span className="text-[11px] text-[var(--text-secondary)]">{t('workflowEditor.config.hoursAfter')}</span>
 </div>
 </div>

 <div>
 <ConfigLabel>{t('workflowEditor.config.priority')}</ConfigLabel>
 <ConfigSelect
 value={config.priority || 'normal'}
 onChange={(e) => onUpdate({ priority: e.target.value })}
 >
 <option value="low">Niedrig</option>
 <option value="normal">Normal</option>
 <option value="high">Hoch</option>
 </ConfigSelect>
 </div>

 <div>
 <ConfigLabel>{t('workflowEditor.config.category')}</ConfigLabel>
 <ConfigSelect
 value={config.category || 'call'}
 onChange={(e) => onUpdate({ category: e.target.value })}
 >
 <option value="call">Anruf</option>
 <option value="email">E-Mail</option>
 <option value="meeting">Meeting</option>
 <option value="other">Sonstige</option>
 </ConfigSelect>
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// CONFIG: Stage
// ═══════════════════════════════════════════════════════════════

function StageConfig({ config, onUpdate, t }) {
 return (
 <div className="space-y-4">
 <div>
 <ConfigLabel>{t('workflowEditor.config.targetStage')}</ConfigLabel>
 <ConfigSelect
 value={config.target_stage || ''}
 onChange={(e) => onUpdate({ target_stage: e.target.value })}
 >
 <option value="">— Auswählen —</option>
 {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
 </ConfigSelect>
 </div>

 <ConfigToggle
 checked={config.log_activity !== false}
 onChange={(v) => onUpdate({ log_activity: v })}
 label={t('workflowEditor.config.logActivity')}
 />

 <div>
 <ConfigLabel>{t('workflowEditor.config.note')}</ConfigLabel>
 <textarea
 value={config.note || ''}
 onChange={(e) => onUpdate({ note: e.target.value })}
 rows={3}
 className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] resize-none"
 placeholder="Optional..."
 />
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// CONFIG: Field
// ═══════════════════════════════════════════════════════════════

const LEAD_FIELDS = [
 { value: 'score', label: 'Score', type: 'number' },
 { value: 'stage', label: 'Stage', type: 'select', options: ['Neu', 'Kontaktiert', 'Termin', 'Angebot', 'Gewonnen', 'Verloren', 'Kalt'] },
 { value: 'segment', label: 'Segment', type: 'select', options: ['Start-Up', 'Mid-Scale', 'Enterprise'] },
 { value: 'volumen', label: 'Volumen', type: 'number' },
 { value: 'prioritaet', label: 'Priorität', type: 'select', options: ['niedrig', 'normal', 'hoch'] },
 { value: 'branche', label: 'Branche', type: 'text' },
 { value: 'firma', label: 'Firma', type: 'text' },
 { value: 'tags', label: 'Tags', type: 'text' },
 { value: 'hat_geantwortet', label: 'Hat geantwortet', type: 'select', options: ['ja', 'nein'] },
 { value: 'email_geoeffnet', label: 'E-Mail geöffnet', type: 'select', options: ['ja', 'nein'] },
 { value: 'link_geklickt', label: 'Link geklickt', type: 'select', options: ['ja', 'nein'] },
 { value: 'tage_seit_letzter_aktivitaet', label: 'Tage seit letzter Aktivität', type: 'number' },
 { value: 'notizen', label: 'Notizen', type: 'text' },
];

function FieldConfig({ config, onUpdate, t }) {
 return (
 <div className="space-y-4">
 <div>
 <ConfigLabel>{t('workflowEditor.config.fieldName')}</ConfigLabel>
 <ConfigSelect
 value={config.field_name || ''}
 onChange={(e) => onUpdate({ field_name: e.target.value })}
 >
 <option value="">— Feld auswählen —</option>
 {LEAD_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
 </ConfigSelect>
 </div>

 <div>
 <ConfigLabel>{t('workflowEditor.config.operation')}</ConfigLabel>
 <ConfigSelect
 value={config.operation || 'set'}
 onChange={(e) => onUpdate({ operation: e.target.value })}
 >
 <option value="set">Setzen</option>
 <option value="increment">Erhöhen um</option>
 <option value="decrement">Verringern um</option>
 <option value="clear">Leeren</option>
 </ConfigSelect>
 </div>

 {config.operation !== 'clear' && (
 <div>
 <ConfigLabel>{t('workflowEditor.config.value')}</ConfigLabel>
 <ConfigInput
 value={config.value || ''}
 onChange={(e) => onUpdate({ value: e.target.value })}
 placeholder="Wert..."
 />
 </div>
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// CONFIG: Webhook
// ═══════════════════════════════════════════════════════════════

function WebhookConfig({ config, variables, onUpdate, t }) {
 const [headerKey, setHeaderKey] = useState('');
 const [headerVal, setHeaderVal] = useState('');

 function addHeader() {
 if (!headerKey.trim()) return;
 onUpdate({ headers: { ...(config.headers || {}), [headerKey]: headerVal } });
 setHeaderKey('');
 setHeaderVal('');
 }

 function removeHeader(key) {
 const h = { ...(config.headers || {}) };
 delete h[key];
 onUpdate({ headers: h });
 }

 return (
 <div className="space-y-4">
 <div>
 <ConfigLabel>{t('workflowEditor.config.url')}</ConfigLabel>
 <ConfigInput
 value={config.url || ''}
 onChange={(e) => onUpdate({ url: e.target.value })}
 placeholder="https://..."
 />
 </div>

 <div>
 <ConfigLabel>{t('workflowEditor.config.method')}</ConfigLabel>
 <ConfigSelect
 value={config.method || 'POST'}
 onChange={(e) => onUpdate({ method: e.target.value })}
 className="w-28"
 >
 <option value="GET">GET</option>
 <option value="POST">POST</option>
 <option value="PUT">PUT</option>
 </ConfigSelect>
 </div>

 {/* Headers */}
 <div>
 <ConfigLabel>{t('workflowEditor.config.headers')}</ConfigLabel>
 {Object.entries(config.headers || {}).map(([k, v]) => (
 <div key={k} className="flex items-center gap-1 mb-1">
 <span className="text-[11px] font-mono text-[var(--text-secondary)] flex-1 truncate">{k}: {v}</span>
 <button onClick={() => removeHeader(k)} className="text-red-400 hover:text-red-600">
 <X className="w-3 h-3" />
 </button>
 </div>
 ))}
 <div className="flex gap-1 mt-1">
 <input
 value={headerKey}
 onChange={(e) => setHeaderKey(e.target.value)}
 placeholder="Key"
 className="flex-1 px-2 py-1 rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[11px] focus:outline-none focus:border-[#338edc]"
 />
 <input
 value={headerVal}
 onChange={(e) => setHeaderVal(e.target.value)}
 placeholder="Value"
 className="flex-1 px-2 py-1 rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[11px] focus:outline-none focus:border-[#338edc]"
 />
 <button onClick={addHeader} className="px-2 py-1 text-[11px] text-[#338edc] hover:bg-[#EBF4FD] rounded-lg">
 <Plus className="w-3 h-3" />
 </button>
 </div>
 </div>

 {/* Body */}
 <div>
 <ConfigLabel>{t('workflowEditor.config.body')}</ConfigLabel>
 <textarea
 value={config.body || ''}
 onChange={(e) => onUpdate({ body: e.target.value })}
 rows={4}
 className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[12px] text-[var(--text-primary)] font-mono focus:outline-none focus:border-[#338edc] resize-none"
 placeholder='{"lead_id": "{{lead.id}}"}'
 />
 </div>

 <div className="flex gap-3">
 <div className="flex-1">
 <ConfigLabel>{t('workflowEditor.config.timeout')}</ConfigLabel>
 <ConfigInput
 type="number"
 value={config.timeout_seconds || 10}
 onChange={(e) => onUpdate({ timeout_seconds: parseInt(e.target.value) || 10 })}
 />
 </div>
 <div className="flex-1">
 <ConfigLabel>{t('workflowEditor.config.retries')}</ConfigLabel>
 <ConfigInput
 type="number"
 value={config.retry_count || 0}
 onChange={(e) => onUpdate({ retry_count: parseInt(e.target.value) || 0 })}
 />
 </div>
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// CONFIG: Condition (enhanced with field-type-aware inputs)
// ═══════════════════════════════════════════════════════════════

const OPERATORS_BY_TYPE = {
 select: [
 { value: '==', label: 'ist gleich' },
 { value: '!=', label: 'ist nicht gleich' },
 ],
 number: [
 { value: '==', label: '=' },
 { value: '!=', label: '≠' },
 { value: '>', label: '>' },
 { value: '<', label: '<' },
 { value: '>=', label: '≥' },
 { value: '<=', label: '≤' },
 ],
 text: [
 { value: '==', label: 'ist gleich' },
 { value: '!=', label: 'ist nicht gleich' },
 { value: 'contains', label: 'enthält' },
 { value: 'starts_with', label: 'beginnt mit' },
 { value: 'is_empty', label: 'ist leer' },
 { value: 'is_not_empty', label: 'ist nicht leer' },
 ],
};

function ConditionConfig({ config, onUpdate, t }) {
 const rules = config.rules || [{ field: '', op: '==', value: '' }];
 const logic = config.logic || 'AND';

 function updateRule(idx, key, value) {
 const newRules = [...rules];
 newRules[idx] = { ...newRules[idx], [key]: value };
 // Reset op and value when field changes
 if (key === 'field') {
 const fieldDef = LEAD_FIELDS.find(f => f.value === value);
 newRules[idx].op = '==';
 newRules[idx].value = fieldDef?.type === 'number' ? '0' : '';
 }
 onUpdate({ rules: newRules });
 }

 function addRule() {
 onUpdate({ rules: [...rules, { field: '', op: '==', value: '' }] });
 }

 function removeRule(idx) {
 if (rules.length <= 1) return;
 onUpdate({ rules: rules.filter((_, i) => i !== idx) });
 }

 return (
 <div className="space-y-3">
 <ConfigLabel>{t('workflowEditor.config.rules')}</ConfigLabel>

 {rules.map((rule, idx) => {
 const fieldDef = LEAD_FIELDS.find(f => f.value === rule.field);
 const ops = OPERATORS_BY_TYPE[fieldDef?.type || 'text'] || OPERATORS_BY_TYPE.text;

 return (
 <div key={idx}>
 {/* AND/OR badge between rules */}
 {idx > 0 && (
 <div className="flex items-center justify-center py-1.5">
 <button
 onClick={() => onUpdate({ logic: logic === 'AND' ? 'OR' : 'AND' })}
 className={`px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full cursor-pointer transition-colors ${
 logic === 'AND'
 ? 'bg-[#338edc]/10 text-[#338edc] dark:bg-[#338edc]/20 dark:text-[#66ABE5]'
 : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
 }`}
 >
 {logic === 'AND' ? t('workflowEditor.conditions.and') : t('workflowEditor.conditions.or')}
 </button>
 </div>
 )}

 {/* Rule card */}
 <div className="flex gap-2 items-start">
 <div className="flex-1 space-y-1.5">
 {/* Field */}
 <ConfigSelect
 value={rule.field || ''}
 onChange={(e) => updateRule(idx, 'field', e.target.value)}
 className="!text-[11px] !py-1.5"
 >
 <option value="">{t('workflowEditor.conditions.selectField')}</option>
 {LEAD_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
 </ConfigSelect>

 <div className="flex gap-1.5">
 {/* Operator */}
 <ConfigSelect
 value={rule.op || '=='}
 onChange={(e) => updateRule(idx, 'op', e.target.value)}
 className="w-32 !text-[11px] !py-1.5"
 >
 {ops.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
 </ConfigSelect>

 {/* Value — type-aware */}
 {!['is_empty', 'is_not_empty'].includes(rule.op) && (
 fieldDef?.type === 'select' ? (
 <ConfigSelect
 value={rule.value || ''}
 onChange={(e) => updateRule(idx, 'value', e.target.value)}
 className="flex-1 !text-[11px] !py-1.5"
 >
 <option value="">{t('workflowEditor.conditions.selectValue')}</option>
 {(fieldDef.options || []).map(o => <option key={o} value={o}>{o}</option>)}
 </ConfigSelect>
 ) : (
 <input
 type={fieldDef?.type === 'number' ? 'number' : 'text'}
 value={rule.value || ''}
 onChange={(e) => updateRule(idx, 'value', e.target.value)}
 placeholder={t('workflowEditor.config.value') + '...'}
 className="flex-1 px-2 py-1.5 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[11px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
 />
 )
 )}
 </div>
 </div>

 {/* Remove */}
 {rules.length > 1 && (
 <button
 onClick={() => removeRule(idx)}
 className="mt-1.5 w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
 >
 <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
 </button>
 )}
 </div>
 </div>
 );
 })}

 {/* Add rule */}
 <button
 onClick={addRule}
 className="w-full py-2 text-[11px] border-2 border-dashed border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)] rounded-lg text-[var(--text-tertiary)] hover:border-[#338edc] hover:text-[#338edc] hover:bg-[#EBF4FD] dark:hover:bg-[#338edc]/10 transition-all"
 >
 + {t('workflowEditor.conditions.addCondition')}
 </button>

 {/* Hint */}
 <div className="text-[11px] text-[var(--text-tertiary)] bg-[var(--bg-inset)] p-2.5 rounded-lg">
 {t('workflowEditor.conditions.hint')}
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// EXECUTION LOG
// ═══════════════════════════════════════════════════════════════

const EXEC_STATUS_CONFIG = {
 running: { label: 'Running', color: '#338edc', bg: '#EBF4FD', darkBg: 'rgba(51,142,220,0.12)', icon: Activity },
 completed: { label: 'Completed', color: '#10B981', bg: '#ECFDF5', darkBg: 'rgba(16,185,129,0.12)', icon: CheckCircle },
 failed: { label: 'Failed', color: '#EF4444', bg: '#FEF2F2', darkBg: 'rgba(239,68,68,0.12)', icon: XCircle },
 cancelled: { label: 'Cancelled', color: '#9CA3AF', bg: '#F3F4F6', darkBg: 'rgba(156,163,175,0.12)', icon: Square },
};

const EXEC_TABS = [
 { key: 'all', label: 'all' },
 { key: 'running', label: 'running' },
 { key: 'completed', label: 'completed' },
 { key: 'failed', label: 'failed' },
];

function ExecutionLog({ workflowId, t }) {
 const [executions, setExecutions] = useState([]);
 const [statusFilter, setStatusFilter] = useState('all');
 const [loading, setLoading] = useState(true);
 const [selectedExec, setSelectedExec] = useState(null);

 useEffect(() => { loadExecutions(); }, [workflowId, statusFilter]);

 async function loadExecutions() {
 setLoading(true);
 try {
 const params = new URLSearchParams({ limit: '50' });
 if (statusFilter !== 'all') params.set('status', statusFilter);
 const data = await api(`/api/sales/workflow-builder/${workflowId}/executions?${params}`);
 setExecutions(data.executions || data || []);
 } catch (e) { console.error('Load executions failed:', e); }
 setLoading(false);
 }

 async function cancelExecution(execId) {
 try {
 await api(`/api/sales/workflow-executions/${execId}/cancel`, { method: 'POST' });
 loadExecutions();
 } catch (e) { console.error('Cancel failed:', e); }
 }

 function formatDate(d) {
 if (!d) return '—';
 return new Date(d).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
 }

 return (
 <div className="flex-1 overflow-y-auto p-6">
 {/* Filter Tabs */}
 <div className="flex items-center gap-1 mb-4 bg-[var(--bg-inset)] rounded-[10px] p-1 border border-[var(--border-card)] w-fit">
 {EXEC_TABS.map(tab => (
 <button
 key={tab.key}
 onClick={() => setStatusFilter(tab.key)}
 className={`px-3 py-1.5 text-[12px] font-medium rounded-[8px] transition-all ${
 statusFilter === tab.key
 ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm'
 : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
 }`}
 >
 {t(`workflowEditor.executions.${tab.label}`)}
 </button>
 ))}
 <button
 onClick={loadExecutions}
 className="ml-2 p-1.5 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors"
 title="Refresh"
 >
 <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
 </button>
 </div>

 {/* Table */}
 {loading ? (
 <div className="text-center py-12 text-[var(--text-tertiary)] text-sm">Loading...</div>
 ) : executions.length === 0 ? (
 <div className="text-center py-12">
 <Activity className="w-10 h-10 text-[var(--text-tertiary)] mx-auto mb-3" strokeWidth={1} />
 <div className="text-[13px] text-[var(--text-secondary)]">{t('workflowEditor.executions.noExecutions')}</div>
 </div>
 ) : (
 <div className="rounded-xl border border-[var(--border-card)] overflow-hidden">
 <table className="w-full text-[12px]">
 <thead>
 <tr className="bg-[var(--bg-inset)] text-[var(--text-secondary)] text-left">
 <th className="px-4 py-2.5 font-medium">Lead</th>
 <th className="px-4 py-2.5 font-medium">Status</th>
 <th className="px-4 py-2.5 font-medium">{t('workflowEditor.executions.started')}</th>
 <th className="px-4 py-2.5 font-medium">{t('workflowEditor.executions.currentStep')}</th>
 <th className="px-4 py-2.5 font-medium w-20"></th>
 </tr>
 </thead>
 <tbody className="divide-y divide-[var(--border-card)]">
 {executions.map(exec => {
 const sc = EXEC_STATUS_CONFIG[exec.status] || EXEC_STATUS_CONFIG.cancelled;
 const StatusIcon = sc.icon;
 return (
 <tr
 key={exec.id}
 onClick={() => setSelectedExec(exec)}
 className="cursor-pointer hover:bg-[var(--bg-hover)] transition-colors"
 >
 <td className="px-4 py-3">
 <div className="font-medium text-[var(--text-primary)]">{exec.lead_name || exec.lead_firma || `Lead #${exec.lead_id}`}</div>
 {exec.lead_firma && exec.lead_name && (
 <div className="text-[11px] text-[var(--text-tertiary)]">{exec.lead_firma}</div>
 )}
 </td>
 <td className="px-4 py-3">
 <span
 className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium"
 style={{ background: sc.bg, color: sc.color }}
 >
 <StatusIcon className="w-3 h-3" strokeWidth={1.5} />
 {t(`workflowEditor.executions.${exec.status}`)}
 </span>
 </td>
 <td className="px-4 py-3 text-[var(--text-secondary)]">{formatDate(exec.started_at || exec.created_at)}</td>
 <td className="px-4 py-3 text-[var(--text-secondary)]">
 {exec.current_step_index != null ? `Step ${exec.current_step_index + 1}` : '—'}
 </td>
 <td className="px-4 py-3">
 {exec.status === 'running' && (
 <button
 onClick={(e) => { e.stopPropagation(); cancelExecution(exec.id); }}
 className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
 title={t('workflowEditor.executions.cancel')}
 >
 <Square className="w-3.5 h-3.5" strokeWidth={1.5} />
 </button>
 )}
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 )}

 {/* Execution Detail Modal */}
 {selectedExec && (
 <ExecutionDetail
 execution={selectedExec}
 onClose={() => setSelectedExec(null)}
 t={t}
 />
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// EXECUTION DETAIL MODAL
// ═══════════════════════════════════════════════════════════════

function ExecutionDetail({ execution, onClose, t }) {
 const [detail, setDetail] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 loadDetail();
 }, [execution.id]);

 async function loadDetail() {
 setLoading(true);
 try {
 const data = await api(`/api/sales/workflow-executions/${execution.id}`);
 setDetail(data);
 } catch (e) { console.error('Load detail failed:', e); }
 setLoading(false);
 }

 function formatDate(d) {
 if (!d) return '—';
 return new Date(d).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
 }

 const sc = EXEC_STATUS_CONFIG[execution.status] || EXEC_STATUS_CONFIG.cancelled;

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
 <div
 onClick={(e) => e.stopPropagation()}
 className="w-full max-w-2xl max-h-[80vh] bg-[var(--bg-card)] rounded-2xl border border-[var(--border-card)] shadow-[var(--shadow-modal)] overflow-hidden flex flex-col"
 >
 {/* Header */}
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card)]">
 <div>
 <div className="text-[14px] font-semibold text-[var(--text-primary)]">
 {t('workflowEditor.executions.detail')} — {execution.lead_name || `Lead #${execution.lead_id}`}
 </div>
 <div className="flex items-center gap-2 mt-1">
 <span
 className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium"
 style={{ background: sc.bg, color: sc.color }}
 >
 {t(`workflowEditor.executions.${execution.status}`)}
 </span>
 <span className="text-[11px] text-[var(--text-tertiary)]">
 {t('workflowEditor.executions.started')}: {formatDate(execution.started_at || execution.created_at)}
 </span>
 </div>
 </div>
 <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors">
 <X className="w-5 h-5" strokeWidth={1.5} />
 </button>
 </div>

 {/* Body — Step Timeline */}
 <div className="flex-1 overflow-y-auto px-6 py-4">
 {loading ? (
 <div className="text-center py-8 text-[var(--text-tertiary)] text-sm">Loading...</div>
 ) : (
 <div className="space-y-0">
 {(detail?.step_log || detail?.steps || []).map((step, i) => {
 const stepSc = step.status === 'completed' ? EXEC_STATUS_CONFIG.completed
 : step.status === 'failed' ? EXEC_STATUS_CONFIG.failed
 : step.status === 'running' ? EXEC_STATUS_CONFIG.running
 : EXEC_STATUS_CONFIG.cancelled;
 const StepStatusIcon = stepSc.icon;
 const typeInfo = STEP_TYPES[step.type] || {};
 const TypeIcon = typeInfo.icon || Activity;

 return (
 <div key={i} className="flex gap-3">
 {/* Timeline line */}
 <div className="flex flex-col items-center">
 <div
 className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
 style={{ background: stepSc.bg, border: `1.5px solid ${stepSc.color}40` }}
 >
 <StepStatusIcon className="w-3.5 h-3.5" style={{ color: stepSc.color }} strokeWidth={1.5} />
 </div>
 {i < (detail?.step_log || detail?.steps || []).length - 1 && (
 <div className="w-0.5 flex-1 min-h-[24px] bg-[var(--border-card)]" />
 )}
 </div>

 {/* Step Content */}
 <div className="flex-1 pb-4">
 <div className="flex items-center gap-2">
 <TypeIcon className="w-3.5 h-3.5" style={{ color: typeInfo.color || '#6B7280' }} strokeWidth={1.5} />
 <span className="text-[13px] font-medium text-[var(--text-primary)]">
 {t(`workflowEditor.stepTypes.${step.type}`) || step.type}
 </span>
 <span className="text-[11px] text-[var(--text-tertiary)]">
 {formatDate(step.executed_at || step.timestamp)}
 </span>
 </div>

 {/* Step details */}
 {step.result && (
 <div className="mt-1 text-[11px] text-[var(--text-secondary)] bg-[var(--bg-inset)] p-2 rounded-lg">
 {typeof step.result === 'string' ? step.result : JSON.stringify(step.result, null, 2)}
 </div>
 )}

 {/* Error message */}
 {step.error && (
 <div className="mt-1 text-[11px] text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg flex items-start gap-1.5">
 <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
 {step.error}
 </div>
 )}

 {/* Email sent details */}
 {step.type === 'email' && step.email_log && (
 <div className="mt-1.5 text-[11px] text-[var(--text-secondary)] bg-[var(--bg-inset)] p-2 rounded-lg">
 <div><strong>An:</strong> {step.email_log.to}</div>
 <div><strong>Betreff:</strong> {step.email_log.subject}</div>
 {step.email_log.opened && <div className="text-green-600 dark:text-green-400">Geöffnet: {formatDate(step.email_log.opened_at)}</div>}
 </div>
 )}
 </div>
 </div>
 );
 })}

 {(!detail?.step_log?.length && !detail?.steps?.length) && (
 <div className="text-center py-8 text-[var(--text-tertiary)] text-[12px]">
 {t('workflowEditor.executions.noSteps')}
 </div>
 )}
 </div>
 )}
 </div>
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// TEST RUN MODAL
// ═══════════════════════════════════════════════════════════════

function TestRunModal({ workflowId, steps, onClose, t }) {
 const [leads, setLeads] = useState([]);
 const [selectedLead, setSelectedLead] = useState(null);
 const [searchQuery, setSearchQuery] = useState('');
 const [simResult, setSimResult] = useState(null);
 const [simLoading, setSimLoading] = useState(false);

 useEffect(() => {
 api('/api/leads?limit=20').then(data => setLeads(data.leads || [])).catch(() => {});
 }, []);

 async function runTest() {
 if (!selectedLead) return;
 setSimLoading(true);
 setSimResult(null);
 try {
 const data = await api(`/api/sales/workflow-builder/${workflowId}/test`, {
 method: 'POST',
 body: { lead_id: selectedLead, dry_run: true }
 });
 setSimResult(data);
 } catch (e) {
 console.error('Test failed:', e);
 setSimResult({ error: e.message });
 }
 setSimLoading(false);
 }

 const filteredLeads = leads.filter(l => {
 if (!searchQuery) return true;
 const q = searchQuery.toLowerCase();
 return (l.ansprechpartner || '').toLowerCase().includes(q) ||
 (l.firma || '').toLowerCase().includes(q) ||
 (l.email || '').toLowerCase().includes(q);
 });

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
 <div
 onClick={(e) => e.stopPropagation()}
 className="w-full max-w-xl max-h-[80vh] bg-[var(--bg-card)] rounded-2xl border border-[var(--border-card)] shadow-[var(--shadow-modal)] overflow-hidden flex flex-col"
 >
 {/* Header */}
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card)]">
 <div className="text-[14px] font-semibold text-[var(--text-primary)]">
 {t('workflowEditor.testRun.title')}
 </div>
 <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors">
 <X className="w-5 h-5" strokeWidth={1.5} />
 </button>
 </div>

 {/* Body */}
 <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
 {/* Lead Selector */}
 <div>
 <ConfigLabel>{t('workflowEditor.testRun.selectLead')}</ConfigLabel>
 <div className="relative mb-2">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-tertiary)]" strokeWidth={1.5} />
 <input
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 placeholder={t('workflowEditor.testRun.searchPlaceholder')}
 className="w-full pl-9 pr-3 py-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
 />
 </div>

 <div className="max-h-40 overflow-y-auto rounded-[10px] border border-[var(--border-card)]">
 {filteredLeads.map(lead => (
 <button
 key={lead.id}
 onClick={() => setSelectedLead(lead.id)}
 className={`w-full flex items-center justify-between px-3 py-2 text-left text-[12px] transition-colors ${
 selectedLead === lead.id
 ? 'bg-[#EBF4FD] dark:bg-[#338edc]/10 text-[#338edc]'
 : 'hover:bg-[var(--bg-hover)] text-[var(--text-primary)]'
 }`}
 >
 <div>
 <div className="font-medium">{lead.ansprechpartner || lead.firma || `Lead #${lead.id}`}</div>
 {lead.firma && lead.ansprechpartner && (
 <div className="text-[11px] text-[var(--text-tertiary)]">{lead.firma}</div>
 )}
 </div>
 {lead.stage && (
 <span className="text-[10px] text-[var(--text-tertiary)] bg-[var(--bg-inset)] px-1.5 py-0.5 rounded">
 {lead.stage}
 </span>
 )}
 </button>
 ))}
 {filteredLeads.length === 0 && (
 <div className="px-3 py-4 text-center text-[12px] text-[var(--text-tertiary)]">
 {t('workflowEditor.testRun.noLeads')}
 </div>
 )}
 </div>
 </div>

 {/* Run Button */}
 <button
 onClick={runTest}
 disabled={!selectedLead || simLoading}
 className="w-full flex items-center justify-center gap-2 py-2.5 text-[13px] font-semibold text-white bg-[#338edc] rounded-[10px] hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
 >
 <Play className="w-4 h-4" strokeWidth={1.5} />
 {simLoading ? t('workflowEditor.testRun.running') : t('workflowEditor.testRun.dryRun')}
 </button>

 {/* Simulation Result */}
 {simResult && !simResult.error && (
 <div>
 <div className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">
 {t('workflowEditor.testRun.simulationResult')}
 </div>
 <SimulationTree steps={simResult.simulation || simResult.steps || []} t={t} />
 </div>
 )}

 {simResult?.error && (
 <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-[12px] text-red-600 dark:text-red-400">
 <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
 <div>
 <div className="font-medium">{t('workflowEditor.testRun.error')}</div>
 <div className="mt-0.5">{simResult.error}</div>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// SIMULATION TREE (recursive)
// ═══════════════════════════════════════════════════════════════

function SimulationTree({ steps, t, depth = 0 }) {
 return (
 <div className={depth > 0 ? 'ml-4 border-l-2 border-[var(--border-card)] pl-3' : ''}>
 {(steps || []).map((step, i) => {
 const typeInfo = STEP_TYPES[step.type] || {};
 const TypeIcon = typeInfo.icon || Activity;
 const isCondition = step.type === 'condition';
 const evaluated = step.evaluated;

 return (
 <div key={i} className="mb-2">
 <div className={`flex items-center gap-2 p-2 rounded-lg text-[12px] ${
 step.active_path === false
 ? 'opacity-40 bg-[var(--bg-inset)]'
 : 'bg-[var(--bg-card)] border border-[var(--border-card)]'
 }`}>
 <div
 className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
 style={{ background: `${typeInfo.color || '#6B7280'}15` }}
 >
 <TypeIcon className="w-3 h-3" style={{ color: typeInfo.color || '#6B7280' }} strokeWidth={1.5} />
 </div>
 <span className="font-medium text-[var(--text-primary)] flex-1">
 {t(`workflowEditor.stepTypes.${step.type}`) || step.type}
 </span>

 {isCondition && evaluated !== undefined && (
 <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
 evaluated ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
 : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
 }`}>
 {evaluated ? '✓ TRUE' : '✗ FALSE'}
 </span>
 )}

 {step.would_execute !== undefined && (
 <span className={`text-[10px] px-1.5 py-0.5 rounded ${
 step.would_execute
 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
 : 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] dark:bg-[var(--bg-inset,#F8F9FC)] dark:text-[var(--text-tertiary,#6B7280)]'
 }`}>
 {step.would_execute ? t('workflowEditor.testRun.wouldExecute') : t('workflowEditor.testRun.skipped')}
 </span>
 )}
 </div>

 {/* Condition branches */}
 {isCondition && (
 <div className="flex gap-2 mt-1.5">
 {step.yes_steps && (
 <div className={`flex-1 ${evaluated === true ? '' : 'opacity-40'}`}>
 <div className="text-[10px] font-bold text-green-600 dark:text-green-400 uppercase mb-1 ml-4">
 ✓ {t('workflowEditor.yesBranch')}
 </div>
 <SimulationTree steps={step.yes_steps} t={t} depth={depth + 1} />
 </div>
 )}
 {step.no_steps && (
 <div className={`flex-1 ${evaluated === false ? '' : 'opacity-40'}`}>
 <div className="text-[10px] font-bold text-red-600 dark:text-red-400 uppercase mb-1 ml-4">
 ✗ {t('workflowEditor.noBranch')}
 </div>
 <SimulationTree steps={step.no_steps} t={t} depth={depth + 1} />
 </div>
 )}
 </div>
 )}
 </div>
 );
 })}
 </div>
 );
}
